import {
  getSupabaseAuthRedirectUrl,
  getSupabaseClient,
  isSupabaseConfigured,
} from '../../src/services/supabaseClient.js';

const DRAFT_BUCKET='catalog-drafts';
const PUBLIC_BUCKET='catalog-public';
const R2_PUBLISH_API_URL=String(import.meta.env.VITE_CATALOG_PUBLISH_API_URL||'')
  .trim()
  .replace(/\/+$/,'');
const EDIT_ROLES=new Set(['editor','reviewer','admin']);
const PUBLISH_ROLES=new Set(['reviewer','admin']);

function jsonClone(value){
  return JSON.parse(JSON.stringify(value??null));
}

function safePathPart(value,fallback='asset'){
  return String(value||fallback)
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-zA-Z0-9._-]+/g,'-')
    .replace(/^-+|-+$/g,'')
    .toLowerCase()||fallback;
}

function isBlob(value){
  return typeof Blob!=='undefined'&&value instanceof Blob;
}

async function sha256(file){
  if(!isBlob(file)||!globalThis.crypto?.subtle)return null;
  const digest=await globalThis.crypto.subtle.digest('SHA-256',await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte)=>byte.toString(16).padStart(2,'0')).join('');
}

function itemAssetFiles(item){
  const files=[];
  if(item.type==='vehicle'){
    if(isBlob(item.bodyFile))files.push({slot:'body',file:item.bodyFile});
    if(isBlob(item.wheelFile))files.push({slot:'wheel-reference',file:item.wheelFile});
  }else if(isBlob(item.assetFile)){
    files.push({slot:'asset',file:item.assetFile});
  }
  if(isBlob(item.audioFile))files.push({slot:'audio',file:item.audioFile});
  if(item.audioFiles&&typeof item.audioFiles==='object'){
    Object.entries(item.audioFiles).forEach(([name,file])=>{
      if(isBlob(file))files.push({slot:`audio-${safePathPart(name)}`,file});
    });
  }
  if(isBlob(item.thumbnailFile))files.push({slot:'thumbnail',file:item.thumbnailFile});
  return files;
}

function itemFromRows(row,version,offer=null){
  return {
    id:row.id,
    type:row.type,
    name:row.name,
    description:row.description||'',
    status:row.status,
    rarity:row.rarity,
    starter:row.starter===true,
    purchasable:Boolean(offer?.active)&&row.starter!==true,
    priceCrowns:Number.isFinite(Number(offer?.price))?Number(offer.price):250,
    profile:jsonClone(version?.profile||{}),
    updatedAt:row.updated_at,
    _remoteVersion:row.current_version||0,
    _thumbnailAsset:jsonClone(version?.asset_manifest?.thumbnail||null),
  };
}

function assetMetadata(item,slot){
  if(slot==='asset')return item.profile?.asset;
  if(slot==='body')return item.profile?.body?.normalization;
  if(slot==='wheel-reference')return item.profile?.wheels?.normalization;
  if(slot==='audio')return item.profile?.sound;
  if(slot.startsWith('audio-'))return item.profile?.sounds?.[slot.slice(6)];
  if(slot==='thumbnail')return {kind:'catalog-thumbnail',width:512,height:512};
  return {};
}

function throwOnError(result,context){
  if(result.error){
    const error=new Error(`${context}: ${result.error.message}`);
    error.cause=result.error;
    throw error;
  }
  return result.data;
}

function catalogIdExistsError(id){
  const error=new Error(`El ID técnico "${id}" ya existe. Elegí otro para no reemplazar el item anterior.`);
  error.code='catalog-id-exists';
  return error;
}

async function publishAssetToR2({asset,blob,item,releaseId,accessToken}){
  const response=await fetch(`${R2_PUBLISH_API_URL}/publish`,{
    method:'PUT',
    headers:{
      Authorization:`Bearer ${accessToken}`,
      'Content-Type':asset.media_type||blob.type||'application/octet-stream',
      'X-Catalog-Release-Id':releaseId,
      'X-Catalog-Item-Type':item.type,
      'X-Catalog-Item-Id':item.id,
      'X-Catalog-Asset-Slot':asset.slot,
      'X-Catalog-File-Name':encodeURIComponent(asset.file_name),
      'X-Catalog-Sha256':asset.sha256||'',
    },
    body:blob,
  });
  const payload=await response.json().catch(()=>null);
  if(!response.ok){
    throw new Error(
      payload?.error
        ?`No se pudo publicar ${asset.file_name} en R2: ${payload.error}`
        :`No se pudo publicar ${asset.file_name} en R2 (${response.status}).`,
    );
  }
  if(!payload?.url||!payload?.path){
    throw new Error(`R2 no devolvió una URL válida para ${asset.file_name}.`);
  }
  return payload;
}

export class SupabaseCatalogRepository{
  constructor(){
    this.client=getSupabaseClient();
    this.configured=isSupabaseConfigured();
    this.session=null;
    this.membership=null;
  }

  async initialize(){
    if(!this.client)return null;
    const {data,error}=await this.client.auth.getSession();
    if(error)throw error;
    this.session=data.session;
    if(this.session)await this.refreshMembership();
    return this.session;
  }

  onAuthStateChange(callback){
    if(!this.client)return {unsubscribe(){}};
    const {data}=this.client.auth.onAuthStateChange((_event,session)=>{
      this.session=session;
      // Evita ejecutar otra consulta de Supabase dentro del callback de Auth.
      setTimeout(async()=>{
        try{
          if(session)await this.refreshMembership();
          else this.membership=null;
          callback?.(session,this.membership);
        }catch(error){
          callback?.(session,null,error);
        }
      },0);
    });
    return data.subscription;
  }

  async signIn(email,password){
    if(!this.client)throw new Error('Supabase no está configurado.');
    const {data,error}=await this.client.auth.signInWithPassword({email,password});
    if(error)throw error;
    this.session=data.session;
    await this.refreshMembership();
    return data;
  }

  async signUpFirstAdmin(email,password){
    if(!this.client)throw new Error('Supabase no está configurado.');
    const {data,error}=await this.client.auth.signUp({
      email,
      password,
      options:{
        data:{display_name:email.split('@')[0]},
        emailRedirectTo:getSupabaseAuthRedirectUrl(),
      },
    });
    if(error)throw error;
    this.session=data.session;
    if(this.session)await this.refreshMembership({claimFirst:true});
    return data;
  }

  async signOut(){
    if(!this.client)return;
    const {error}=await this.client.auth.signOut();
    if(error)throw error;
    this.session=null;
    this.membership=null;
  }

  async refreshMembership({claimFirst=false}={}){
    if(!this.client||!this.session){
      this.membership=null;
      return null;
    }
    let {data,error}=await this.client
      .from('team_members')
      .select('user_id,role,display_name')
      .eq('user_id',this.session.user.id)
      .maybeSingle();
    if(error)throw error;
    if(!data&&claimFirst){
      const claim=await this.client.rpc('claim_catalog_admin');
      if(claim.error)throw claim.error;
      if(claim.data){
        const response=await this.client
          .from('team_members')
          .select('user_id,role,display_name')
          .eq('user_id',this.session.user.id)
          .single();
        data=throwOnError(response,'No se pudo leer el rol inicial');
      }
    }
    this.membership=data||null;
    return this.membership;
  }

  canRead(){
    return Boolean(this.session&&this.membership);
  }

  canEdit(){
    return Boolean(this.session&&EDIT_ROLES.has(this.membership?.role));
  }

  canPublish(){
    return Boolean(this.session&&PUBLISH_ROLES.has(this.membership?.role));
  }

  async list(){
    if(!this.canRead())return [];
    const rows=throwOnError(await this.client
      .from('catalog_items')
      .select('id,type,name,description,rarity,status,starter,current_version,updated_at')
      .order('updated_at',{ascending:false}),'No se pudo leer el catálogo');
    if(!rows.length)return [];
    const [versionsResult,offersResult]=await Promise.all([
      this.client.from('catalog_item_versions')
        .select('item_id,version,profile,asset_manifest')
        .in('item_id',rows.map((item)=>item.id)),
      this.client.from('catalog_offers')
        .select('item_id,currency,price,active')
        .in('item_id',rows.map((item)=>item.id)),
    ]);
    const versions=throwOnError(versionsResult,'No se pudieron leer las versiones');
    const offers=offersResult.error?[]:offersResult.data||[];
    const offerByItem=new Map(offers.map((offer)=>[offer.item_id,offer]));
    const versionByKey=new Map(versions.map((version)=>[
      `${version.item_id}:${version.version}`,
      version,
    ]));
    const items=rows.map((row)=>itemFromRows(
      row,
      versionByKey.get(`${row.id}:${row.current_version}`),
      offerByItem.get(row.id),
    ));
    const thumbnailItems=items.filter((item)=>item._thumbnailAsset?.path);
    if(thumbnailItems.length){
      const paths=thumbnailItems.map((item)=>item._thumbnailAsset.path);
      const signed=await this.client.storage.from(DRAFT_BUCKET).createSignedUrls(paths,3600);
      if(!signed.error){
        const urlByPath=new Map((signed.data||[]).map((entry)=>[entry.path,entry.signedUrl]));
        thumbnailItems.forEach((item)=>{
          item.thumbnailUrl=urlByPath.get(item._thumbnailAsset.path)||null;
        });
      }
    }
    items.forEach((item)=>{delete item._thumbnailAsset;});
    return items;
  }

  async get(id,{downloadAssets=true}={}){
    if(!this.canRead())return null;
    const row=throwOnError(await this.client
      .from('catalog_items')
      .select('id,type,name,description,rarity,status,starter,current_version,updated_at')
      .eq('id',id)
      .maybeSingle(),'No se pudo leer el item');
    if(!row)return null;
    const [versionResult,offerResult]=await Promise.all([
      this.client.from('catalog_item_versions')
        .select('item_id,version,profile,asset_manifest')
        .eq('item_id',id)
        .eq('version',row.current_version)
        .maybeSingle(),
      this.client.from('catalog_offers')
        .select('item_id,currency,price,active')
        .eq('item_id',id)
        .maybeSingle(),
    ]);
    const version=throwOnError(versionResult,'No se pudo leer la versión');
    const offer=offerResult.error?null:offerResult.data;
    const item=itemFromRows(row,version,offer);
    if(!downloadAssets||!version)return item;

    const assets=throwOnError(await this.client
      .from('catalog_assets')
      .select('slot,bucket_id,path,file_name,media_type,byte_size,sha256,metadata')
      .eq('item_id',id)
      .eq('version',row.current_version),'No se pudieron leer los assets');
    await Promise.all(assets.map(async(asset)=>{
      const blob=throwOnError(
        await this.client.storage.from(asset.bucket_id).download(asset.path),
        `No se pudo descargar ${asset.file_name}`,
      );
      const file=new File([blob],asset.file_name,{type:asset.media_type||blob.type});
      if(asset.slot==='body')item.bodyFile=file;
      else if(asset.slot==='wheel-reference')item.wheelFile=file;
      else if(asset.slot==='thumbnail')item.thumbnailFile=file;
      else if(asset.slot==='asset')item.assetFile=file;
      else if(asset.slot==='audio')item.audioFile=file;
      else if(asset.slot.startsWith('audio-')){
        item.audioFiles||={};
        item.audioFiles[asset.slot.slice(6)]=file;
      }
    }));
    delete item._thumbnailAsset;
    return item;
  }

  async currentVersion(id){
    if(!this.canRead())return 0;
    const row=throwOnError(await this.client
      .from('catalog_items')
      .select('current_version')
      .eq('id',id)
      .maybeSingle(),'No se pudo comprobar la versión remota');
    return row?.current_version||0;
  }

  async save(item,{createOnly=false}={}){
    if(!this.canEdit())throw new Error('Tu usuario no tiene permiso para editar el catálogo compartido.');
    const previousVersion=await this.currentVersion(item.id);
    if(createOnly&&previousVersion>0)throw catalogIdExistsError(item.id);
    const expectedPreviousVersion=createOnly?0:previousVersion;
    const nextVersion=expectedPreviousVersion+1;
    const uploadedAssets=[];
    const assetManifest={};

    for(const {slot,file} of itemAssetFiles(item)){
      const fallbackName=slot==='thumbnail'
        ?`${slot}.webp`
        :file.type==='audio/mpeg'||file.type==='audio/mp3'
          ?`${slot}.mp3`
          :`${slot}.glb`;
      const fileName=safePathPart(file.name,fallbackName);
      const token=globalThis.crypto?.randomUUID?.()||Date.now().toString(36);
      const path=[
        'items',
        safePathPart(item.type),
        safePathPart(item.id),
        `v${nextVersion}`,
        `${safePathPart(slot)}-${token}-${fileName}`,
      ].join('/');
      const digest=await sha256(file);
      throwOnError(await this.client.storage.from(DRAFT_BUCKET).upload(path,file,{
        contentType:file.type||'application/octet-stream',
        upsert:false,
      }),`No se pudo subir ${file.name}`);
      const metadata={
        slot,
        path,
        fileName:file.name,
        mediaType:file.type||'application/octet-stream',
        byteSize:file.size,
        sha256:digest,
      };
      assetManifest[slot]=metadata;
      uploadedAssets.push({
        slot,
        path,
        file_name:file.name,
        media_type:metadata.mediaType,
        byte_size:file.size,
        sha256:digest,
        metadata:jsonClone(assetMetadata(item,slot))||{},
      });
    }

    const response=await this.client.rpc('save_catalog_item',{
      p_id:item.id,
      p_type:item.type,
      p_name:item.name||item.id,
      p_description:item.description||'',
      p_rarity:item.rarity||'common',
      p_status:item.status||'draft',
      p_starter:item.starter===true,
      p_profile:jsonClone(item.profile)||{},
      p_asset_manifest:assetManifest,
      p_assets:uploadedAssets,
      p_expected_previous_version:expectedPreviousVersion,
      p_change_notes:'',
    });
    if(response.error){
      await this.client.storage.from(DRAFT_BUCKET).remove(uploadedAssets.map((asset)=>asset.path));
      if(createOnly&&(
        response.error.code==='40001'
        ||/changed remotely/i.test(response.error.message||'')
      ))throw catalogIdExistsError(item.id);
      throwOnError(response,'No se pudo guardar la nueva versión');
    }
    throwOnError(await this.client.rpc('save_catalog_item_economy',{
      p_item_id:item.id,
      p_starter:item.starter===true,
      p_purchasable:item.purchasable!==false,
      p_price:Math.max(1,Math.round(Number(item.priceCrowns)||250)),
    }),'No se pudo guardar la configuración de adquisición');
    return {
      ...item,
      status:item.status==='published'?'review':item.status,
      _remoteVersion:response.data,
    };
  }

  async remove(id){
    if(!this.canEdit())throw new Error('Tu usuario no tiene permiso para eliminar borradores.');
    const item=await this.get(id,{downloadAssets:false});
    if(!item)return;
    const previous=await this.latestRelease();
    const previousItems=Array.isArray(previous?.manifest?.items)?previous.manifest.items:[];
    const wasPublished=previousItems.some((candidate)=>candidate.id===id);
    if(wasPublished){
      if(!this.canPublish()){
        throw new Error('Se necesita rol reviewer o admin para retirar del juego un item publicado.');
      }
      const releaseId=globalThis.crypto.randomUUID();
      const manifest={
        ...jsonClone(previous.manifest),
        schema:'toybox-catalog-release@1',
        releaseId,
        publishedAt:new Date().toISOString(),
        items:previousItems.filter((candidate)=>candidate.id!==id),
      };
      const release=throwOnError(await this.client.rpc('archive_catalog_item',{
        p_item_id:id,
        p_release_id:releaseId,
        p_manifest:manifest,
      }),'No se pudo retirar el item del catálogo publicado');
      const result=Array.isArray(release)?release[0]:release;
      return {archived:true,releaseNumber:result?.release_number||null};
    }
    if(item.status==='published'){
      throwOnError(await this.client.from('catalog_items').update({
        status:'archived',
        updated_at:new Date().toISOString(),
      }).eq('id',id),'No se pudo archivar el item publicado');
      return {archived:true,releaseNumber:null};
    }
    const assets=throwOnError(await this.client
      .from('catalog_assets')
      .select('bucket_id,path')
      .eq('item_id',id),'No se pudieron enumerar los archivos');
    throwOnError(await this.client.from('catalog_items').delete().eq('id',id),'No se pudo eliminar el item');
    const draftPaths=assets.filter((asset)=>asset.bucket_id===DRAFT_BUCKET).map((asset)=>asset.path);
    if(draftPaths.length){
      const removal=await this.client.storage.from(DRAFT_BUCKET).remove(draftPaths);
      if(removal.error)console.warn('El item se eliminó, pero quedaron archivos huérfanos.',removal.error);
    }
    return {archived:false};
  }

  async latestRelease(){
    if(!this.client)return null;
    return throwOnError(await this.client
      .from('catalog_releases')
      .select('id,release_number,manifest,published_at')
      .order('release_number',{ascending:false})
      .limit(1)
      .maybeSingle(),'No se pudo leer la última publicación');
  }

  async normalizeEconomy(starterIds){
    if(!this.canPublish())throw new Error('Se necesita rol reviewer o admin para configurar la economía.');
    const result=throwOnError(await this.client.rpc('normalize_catalog_economy',{
      p_starter_ids:Array.isArray(starterIds)?starterIds:[],
    }),'No se pudo normalizar la economía del catálogo');
    return Array.isArray(result)?result[0]:result;
  }

  async publish(id){
    if(!this.canPublish())throw new Error('Se necesita rol reviewer o admin para publicar.');
    const item=await this.get(id,{downloadAssets:false});
    if(!item)throw new Error('El item ya no existe.');
    const assets=throwOnError(await this.client
      .from('catalog_assets')
      .select('slot,bucket_id,path,file_name,media_type,byte_size,sha256,metadata')
      .eq('item_id',id)
      .eq('version',item._remoteVersion),'No se pudieron leer los assets a publicar');
    const releaseId=globalThis.crypto.randomUUID();
    const publicAssets={};
    const publishToR2=Boolean(R2_PUBLISH_API_URL);

    for(const asset of assets){
      const blob=throwOnError(
        await this.client.storage.from(asset.bucket_id).download(asset.path),
        `No se pudo preparar ${asset.file_name}`,
      );
      let published;
      if(publishToR2){
        published=await publishAssetToR2({
          asset,
          blob,
          item,
          releaseId,
          accessToken:this.session.access_token,
        });
      }else{
        const publicPath=[
          'releases',
          releaseId,
          safePathPart(item.type),
          safePathPart(item.id),
          `${safePathPart(asset.slot)}-${safePathPart(asset.file_name)}`,
        ].join('/');
        throwOnError(await this.client.storage.from(PUBLIC_BUCKET).upload(publicPath,blob,{
          contentType:asset.media_type||blob.type||'application/octet-stream',
          upsert:false,
        }),`No se pudo publicar ${asset.file_name}`);
        const {data}=this.client.storage.from(PUBLIC_BUCKET).getPublicUrl(publicPath);
        published={url:data.publicUrl,path:publicPath};
      }
      publicAssets[asset.slot]={
        url:published.url,
        path:published.path,
        provider:publishToR2?'cloudflare-r2':'supabase-storage',
        fileName:asset.file_name,
        mediaType:asset.media_type,
        byteSize:asset.byte_size,
        sha256:asset.sha256,
        metadata:asset.metadata||{},
      };
    }

    const previous=await this.latestRelease();
    const previousItems=Array.isArray(previous?.manifest?.items)?previous.manifest.items:[];
    const entry={
      id:item.id,
      type:item.type,
      name:item.name,
      description:item.description,
      rarity:item.rarity,
      starter:item.starter===true,
      economy:{
        currency:'crowns',
        purchasable:item.purchasable!==false&&item.starter!==true,
        price:Math.max(1,Math.round(Number(item.priceCrowns)||250)),
      },
      version:item._remoteVersion,
      profile:jsonClone(item.profile)||{},
      assets:publicAssets,
    };
    const manifest={
      schema:'toybox-catalog-release@1',
      releaseId,
      publishedAt:new Date().toISOString(),
      items:[...previousItems.filter((candidate)=>candidate.id!==id),entry]
        .sort((first,second)=>`${first.type}:${first.id}`.localeCompare(`${second.type}:${second.id}`)),
    };
    const release=throwOnError(await this.client.rpc('publish_catalog_item',{
      p_item_id:id,
      p_release_id:releaseId,
      p_manifest:manifest,
    }),'No se pudo publicar el manifiesto');
    return {
      ...(Array.isArray(release)?release[0]:release),
      manifest,
    };
  }
}
